import AlertPopup from "components/alert-popup/AlertPopup";
import Button from "core/buttons/button/Button";
import TemporaryBackupForm from "components/form/mission/TemporaryBackupForm";
import ValidationModal from "components/modal/ValidationModal";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { FormattedMessage } from "react-intl";
import { FILTERS_FOR_NAMES, POPUP_STATUS, QUERY_STATUS } from "constants/constants";
import { TEMPORARY_BACKUP_DEFAULT_VALUES } from "constants/forms";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMissionContext } from "api/mission/MissionProvider";
import { useNavigate, useParams } from "react-router-dom";
import { useUserContext } from "api/user/UserProvider";
import { useMissionBackupContext } from "api/mission/mission-backup/MissionBackupProvider";
import { useGeneralContext } from "api/directory/general/GeneralProvider";

const TemporaryBackup = () => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    clearErrors,
    watch,
  } = useForm({
    defaultValues: {
      ...TEMPORARY_BACKUP_DEFAULT_VALUES,
      manager_id: [],
    },
  });
  const { slug } = useParams();
  const [code, setCode] = useState();
  const [status, setStatus] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cellHighlight, setCellHighlight] = useState(false);

  const { lang, userScope, apiErrorResponse, setApiErrorResponse } = useUserContext();
  const {
    getMissionBySlug,
    missionBySlug,
    editMissionById,
    createMission,
    missionCreated,
  } = useMissionContext();
  const {
    createMissionBackup,
    missionBackupCreatedStatus,
    setMissionBackupCreatedStatus,
  } = useMissionBackupContext();
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const [managerValue, setManagerValue] = useState([]);

  useEffect(() => {
    setApiErrorResponse(null);
    getMissionBySlug({ slug });
    return () => setApiErrorResponse(null);
  }, []);

  const resolveManager = useCallback(async () => {
    if (!missionBySlug?.manager_id) return;
    const result = await getNamesByFilters({
      id: [missionBySlug.manager_id],
      types: [FILTERS_FOR_NAMES.PERSON],
    });
    if (result?.[0]) {
      const mgr = result[0];
      const formatted = [
        { value: mgr.id, label: `${mgr.first_name} ${mgr.last_name}` },
      ];
      setManagerValue(formatted);
      setValue("manager_id", formatted);
    }
  }, [missionBySlug?.manager_id]);

  useEffect(() => {
    resolveManager();
  }, [resolveManager]);
  const navigate = useNavigate();

  const onSubmit = (data) => {
    setIsModalOpen(true);
    createMissionBackup({
      data: {
        worker_id: data?.worker_id[0]?.value,
        original_mission_id: missionBySlug?.id,
        original_end_date: data?.replacement_start_date,
        backup_start_date: data?.arrival_date_replacement,
        backup_end_date: data?.departure_date_of_replacement,
        final_start_date: data?.replacement_end_date,
      },
    });
  };

  const replacementStartDate = watch("replacement_start_date");
  const replacementEndDate = watch("replacement_end_date");

  useEffect(() => {
    if (missionCreated?.id) {
      editMissionById({
        id: missionBySlug?.id,
        data: {
          ...missionBySlug,
          end_date: replacementStartDate,
        },
      });
      createMission({
        ...missionBySlug,
        startDate: replacementEndDate,
        endDate: null,
      });
    }
  }, [missionCreated]);

  const onError = () => {
    setCellHighlight(true);
    setStatus(POPUP_STATUS.ERROR);
    setCode(FIELD_REQUIRED);
  };

  return (
    <>
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <ValidationModal
        open={isModalOpen}
        status={missionBackupCreatedStatus}
        onClose={() => {
          setIsModalOpen(false);
          if (missionBackupCreatedStatus === QUERY_STATUS.SUCCESS) {
            navigate(`/${userScope}/missions/${slug}`);
          }
          setMissionBackupCreatedStatus(QUERY_STATUS.INITIAL);
        }}
        messages={{
          pendingTitleId: "modal_validation_create_file",
          pendingMessageId: "modal_validation_file_pending_msg",
          successTitleId: "modal_validation_create_file",
          successMessageId: "modal_validation_file_success_msg",
          errorTitleId: "modal_validation_create_file",
          errorMessageId: "modal_validation_field_errors_msg",
          errorMessageDefault:
            "Certains champs comportent des erreurs.{br}Veuillez cliquer sur les <nowrap>icônes{errorIcon}pour</nowrap> obtenir plus de détails et corriger les informations nécessaires.",
        }}
      />
      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        style={{ paddingTop: "2rem" }}
      >
        <TemporaryBackupForm
          apiErrorResponse={apiErrorResponse}
          cellHighlight={cellHighlight}
          control={control}
          errors={errors}
          getNamesByFilters={getNamesByFilters}
          namesByFilters={namesByFilters}
          lang={lang}
          managerValue={managerValue}
          register={register}
          setValue={(field, value) => {
            setValue(field, value);
            clearErrors(field);
          }}
          watch={watch}
        />
        <div className="validate-section">
          <Button color="btn-medium gray-700" id="button-form-validation">
            <FormattedMessage id="validate" defaultMessage="Valider" />
          </Button>
        </div>
      </form>
    </>
  );
};

export default TemporaryBackup;
